import { useRef, useState } from 'react';
import { fileUrl, uploadDirect } from '../api/client';
import { useToast } from '../context/ToastContext';
import { colors } from '../theme';

/**
 * Photo picker + uploader used wherever a listing needs images.
 *
 * Handles one image (`multiple={false}`, value is a string) or many (value is
 * an array of URLs). Files upload as soon as they are chosen - by the file
 * dialog or by dropping them on the tile - so the parent form only ever holds
 * hosted URLs, exactly like the mobile app.
 */
const ImageUploader = ({
  label = 'Photos',
  hint,
  value,
  onChange,
  multiple = true,
  max = 6,
  size = 92,
  // Which bucket folder these images belong in (logo, cover, gallery, menu,
  // product, service, document...). Keeps a vendor's uploads sorted in S3.
  folder = 'misc',
}) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const toast = useToast();

  const images = multiple ? (value || []) : (value ? [value] : []);
  const remaining = multiple ? Math.max(max - images.length, 0) : 1;

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    if (multiple && remaining === 0) {
      toast.error(`You can add up to ${max} photos.`);
      return;
    }
    setBusy(true);
    try {
      // Straight to storage with a signed URL: no size limit on a photo.
      const urls = await Promise.all(files.slice(0, remaining || 1).map((f) => uploadDirect(f, folder)));
      if (multiple) onChange([...(value || []), ...urls].slice(0, max));
      else onChange(urls[0]);
    } catch (err) {
      toast.error(err.message || 'Could not upload the photo. Please try again.', 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeAt = (index) => {
    if (multiple) onChange((value || []).filter((_, i) => i !== index));
    else onChange('');
  };

  return (
    <div className="field">
      {label ? <label className="field-label">{label}</label> : null}

      <div className="row wrap" style={{ gap: 10, alignItems: 'flex-start' }}>
        {images.map((url, i) => (
          <div key={`${url}-${i}`} style={{ position: 'relative' }}>
            <img
              src={fileUrl(url)}
              alt=""
              style={{
                width: size, height: size, objectFit: 'cover',
                borderRadius: 12, border: `1px solid ${colors.line}`, background: colors.bg,
              }}
            />
            <button
              type="button"
              onClick={() => removeAt(i)}
              aria-label="Remove photo"
              style={{
                position: 'absolute', top: -6, right: -6,
                width: 22, height: 22, borderRadius: 11, border: 0,
                background: colors.danger, color: '#fff', fontSize: 12, fontWeight: 800,
                display: 'grid', placeItems: 'center',
              }}
            >
              ✕
            </button>
          </div>
        ))}

        {(multiple ? remaining > 0 : images.length === 0) ? (
          <button
            type="button"
            onClick={() => inputRef.current && inputRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              handleFiles(e.dataTransfer.files);
            }}
            disabled={busy}
            style={{
              width: size, height: size, borderRadius: 12,
              border: `1.5px dashed ${dragging ? colors.primary : colors.line}`,
              background: dragging ? colors.primaryLight : '#fff',
              display: 'grid', placeItems: 'center', gap: 2,
              color: colors.muted, fontSize: 11, fontWeight: 600,
            }}
          >
            {busy ? <span className="spinner" style={{ color: colors.primary }} /> : (
              <span style={{ textAlign: 'center', lineHeight: 1.4 }}>
                <span style={{ fontSize: 21, display: 'block' }}>📷</span>
                Add photo
              </span>
            )}
          </button>
        ) : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={(e) => handleFiles(e.target.files)}
        className="sr-only"
        tabIndex={-1}
      />

      {hint ? <div className="field-hint">{hint}</div> : null}
      {multiple ? (
        <div className="field-hint">
          {images.length}/{max} photos · drag and drop works too
        </div>
      ) : null}
    </div>
  );
};

export default ImageUploader;
